import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { toast, Toaster } from "sonner";
import {
  FolderOpen,
  FilePlus2,
  Save,
  Download,
  Upload,
  RotateCcw,
  PanelLeft,
  PanelRight,
  Columns2,
  Cloud,
  Users,
  Loader2,
  UserPlus,
  FileDown,
  FileUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormBuilder } from "@/components/FormBuilder";
import { A4Preview } from "@/components/A4Preview";
import { TAGLINES, DEFAULT_LOGO } from "@/data/catalog";
import { todayISO, formatDateIT } from "@/lib/format";
import { loadLogo, saveLogo, clearLogo } from "@/lib/storage";
import * as api from "@/lib/alberoApi";
import { normalizeRichRuns, hasRichStyles } from "@/lib/richText";

const uid = () =>
  Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

const normalizeDescriptionText = (value) =>
  String(value == null ? "" : value).replace(/\r\n?/g, "\n");

const enrichQuoteWithCatalogRichText = (currentQuote, currentCatalog) => {
  if (!currentQuote || !Array.isArray(currentQuote.items) || !currentQuote.items.length)
    return currentQuote;
  if (!Array.isArray(currentCatalog) || !currentCatalog.length) return currentQuote;

  let changed = false;
  const nextItems = currentQuote.items.map((it) => {
    const existingRuns = normalizeRichRuns(
      it?.description || "",
      it?.descriptionRich || [],
    );

    // Se la voce contiene già vera formattazione, la conserviamo.
    if (hasRichStyles(existingRuns)) return it;

    const itemText = normalizeDescriptionText(it?.description || "");
    let preset = null;

    if (it?.catalogId) {
      preset = currentCatalog.find((entry) => entry.id === it.catalogId) || null;
    }

    // Compatibilità con vecchi preventivi che non avevano catalogId.
    if (!preset && it?.name) {
      preset =
        currentCatalog.find(
          (entry) =>
            String(entry?.name || "") === String(it.name || "") &&
            normalizeDescriptionText(entry?.description || "") === itemText,
        ) || null;
    }

    if (!preset) return it;
    if (normalizeDescriptionText(preset.description || "") !== itemText) return it;

    const rich = normalizeRichRuns(
      preset.description || "",
      preset.descriptionRich ||
        preset.richDescription ||
        preset.descriptionRuns ||
        [],
    );

    if (!hasRichStyles(rich)) return it;

    changed = true;
    return {
      ...it,
      catalogId: it.catalogId || preset.id,
      descriptionRich: rich,
    };
  });

  return changed ? { ...currentQuote, items: nextItems } : currentQuote;
};

const blankQuote = () => ({
  id: uid(),
  number: "",
  date: todayISO(),
  projectTitle: "",
  tagline: TAGLINES[0].text,
  client: { name: "", vat: "", taxCode: "", address: "", cap: "", city: "", province: "", country: "Italia", email: "", phone: "" },
  items: [],
  priceMode: "oneri",
  globalDiscount: 0,
  reservedPrice: false,
  showItemPrices: true,
  roundTotal: "",
  validUntil: "",
  processingTime: "60 giorni dall'accettazione",
  paymentTerms: "30% all'accettazione, saldo alla consegna",
  notes: "",
});

const LAYOUTS = [
  { key: "form", icon: PanelLeft, label: "Editor" },
  { key: "split", icon: Columns2, label: "Split" },
  { key: "preview", icon: PanelRight, label: "Anteprima" },
];

function App() {
  const [quote, setQuote] = useState(blankQuote);
  const [logo, setLogo] = useState(DEFAULT_LOGO);
  const [layout, setLayout] = useState("split");
  const [leftWidth, setLeftWidth] = useState(480);
  const [savedOpen, setSavedOpen] = useState(false);
  const [saved, setSaved] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [apiReady, setApiReady] = useState(false);
  const [apiBusy, setApiBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [clientsOpen, setClientsOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [authPassword, setAuthPassword] = useState(api.getPassword());
  const [authenticated, setAuthenticated] = useState(false);
  const fileRef = useRef(null);
  const importRef = useRef(null);

  const loadOnlineData = useCallback(async () => {
    const [voci, clienti] = await Promise.all([api.listVoci(), api.listClienti()]);
    setCatalog(voci || []);
    setClients(clienti || []);
    setApiReady(true);
  }, []);

  useEffect(() => {
    const stored = loadLogo();
    if (stored) setLogo(stored);

    if (api.getPassword()) {
      api.verify()
        .then(async () => {
          setAuthenticated(true);
          await loadOnlineData();
        })
        .catch(() => {
          api.clearPassword();
          setAuthenticated(false);
          setApiReady(false);
        });
    }
  }, [loadOnlineData]);

  // Ogni volta che il catalogo online viene caricato o aggiornato, riallinea
  // automaticamente il rich text delle voci già presenti nel preventivo.
  // Non sovrascrive descrizioni modificate manualmente.
  useEffect(() => {
    if (!catalog.length) return;
    setQuote((current) => enrichQuoteWithCatalogRichText(current, catalog));
  }, [catalog]);

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!authPassword.trim()) return;
    setApiBusy(true);
    api.setPassword(authPassword.trim());
    try {
      await api.verify();
      setAuthenticated(true);
      await loadOnlineData();
      toast.success("Archivio online collegato");
    } catch {
      api.clearPassword();
      setAuthenticated(false);
      toast.error("Password non corretta");
    } finally {
      setApiBusy(false);
    }
  };

  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = leftWidth;
    const onMove = (ev) => {
      const w = Math.min(880, Math.max(380, startW + ev.clientX - startX));
      setLeftWidth(w);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  };

  const set = useCallback((field, value) => {
    setQuote((q) => ({ ...q, [field]: value }));
  }, []);

  const setClient = useCallback((field, value) => {
    setQuote((q) => ({ ...q, client: { ...q.client, [field]: value } }));
  }, []);

  const items = {
    add: (partial) =>
      setQuote((q) => ({
        ...q,
        items: [
          ...q.items,
          {
            id: uid(),
            category: "",
            name: "",
            description: "",
            descriptionRich: [],
            qty: 1,
            unitPrice: 0,
            discount: 0,
            recurring: false,
            monthlyPrice: 0,
            monthlyQty: 1,
            showPrice: true,
            ...partial,
          },
        ],
      })),
    update: (id, field, value) =>
      setQuote((q) => ({
        ...q,
        items: q.items.map((it) =>
          it.id === id ? { ...it, [field]: value } : it,
        ),
      })),
    setAllShowPrice: (value) =>
      setQuote((q) => ({
        ...q,
        items: q.items.map((it) => ({ ...it, showPrice: value })),
      })),
    remove: (id) =>
      setQuote((q) => ({ ...q, items: q.items.filter((it) => it.id !== id) })),
    move: (from, to) =>
      setQuote((q) => {
        if (from === to || from == null || to == null) return q;
        const arr = [...q.items];
        if (from < 0 || from >= arr.length || to < 0 || to >= arr.length)
          return q;
        const [m] = arr.splice(from, 1);
        arr.splice(to, 0, m);
        return { ...q, items: arr };
      }),
  };

  /* ---- save ---- */
  const handleSave = async () => {
    if (!quote.client?.name) {
      toast.error("Seleziona o inserisci prima un cliente");
      return;
    }
    setApiBusy(true);
    try {
      const result = await api.savePreventivo(quote);
      if (result?.quote) setQuote(result.quote);
      const number = result?.quote?.number || quote.number || "";
      toast.success(`Preventivo ${number} salvato online`);
    } catch (e) {
      toast.error(e.message === "PASSWORD_ERRATA" ? "Sessione scaduta: ricarica e accedi" : "Salvataggio online non riuscito");
    } finally {
      setApiBusy(false);
    }
  };

  const handleNew = () => {
    setQuote(blankQuote());
    toast.info("Nuovo preventivo creato");
  };

  const applyLoaded = (q) => {
    const base = { ...blankQuote(), ...q, items: q?.items || [] };
    setQuote(enrichQuoteWithCatalogRichText(base, catalog));
  };

  const openSaved = async () => {
    setSavedOpen(true);
    setSavedLoading(true);
    try {
      const online = await api.listPreventivi();
      const ordered = [...(online || [])].sort((a, b) =>
        String(b?.date || "").localeCompare(String(a?.date || "")),
      );
      setSaved(ordered);
    } catch {
      toast.error("Errore lettura preventivi online");
      setSaved([]);
    } finally {
      setSavedLoading(false);
    }
  };

  const loadOne = async (entry) => {
    try {
      const full = await api.getPreventivo(entry.fileId);
      applyLoaded(full);
      setSavedOpen(false);
      toast.success(`Preventivo ${entry.number} caricato`);
    } catch {
      toast.error("Caricamento non riuscito");
    }
  };

  /* ---- clients ---- */
  const openClients = async () => {
    setClientsOpen(true);
    setClientSearch("");
    try {
      const list = await api.listClienti();
      setClients(list || []);
    } catch {
      toast.error("Errore lettura rubrica online");
    }
  };

  const pickClient = (c) => {
    setQuote((q) => ({ ...q, client: { ...q.client, ...c } }));
    setClientsOpen(false);
    toast.success(`Cliente «${c.name}» inserito`);
  };

  const saveCurrentClient = async () => {
    if (!quote.client?.name) {
      toast.error("Inserisci prima il nome del cliente");
      return;
    }
    setApiBusy(true);
    try {
      const savedClient = await api.saveCliente(quote.client);
      setQuote((q) => ({ ...q, client: { ...q.client, ...savedClient } }));
      const list = await api.listClienti();
      setClients(list || []);
      toast.success("Cliente salvato nella rubrica online");
    } catch {
      toast.error("Salvataggio cliente non riuscito");
    } finally {
      setApiBusy(false);
    }
  };

  const saveCatalogItem = async (item) => {
    if (!item?.name?.trim()) {
      toast.error("Inserisci prima il nome della voce");
      return null;
    }
    try {
      const savedItem = await api.saveVoce({
        category: item.category || "Personalizzata",
        name: item.name,
        description: item.description || "",
        active: true,
      });
      const voci = await api.listVoci();
      setCatalog(voci || []);
      toast.success("Voce aggiunta al catalogo online");
      return savedItem;
    } catch {
      toast.error("Salvataggio voce nel catalogo non riuscito");
      return null;
    }
  };

  const safeFilePart = (value, fallback) =>
    String(value || fallback || "")
      .replace(/[\\/:*?"<>|#%{}~&]/g, " ")
      .trim()
      .replace(/\s+/g, "_")
      .slice(0, 80);

  const handlePrint = () => {
    const originalTitle = document.title;
    const datePart = safeFilePart(quote.date || todayISO(), todayISO());
    const clientPart = safeFilePart(quote.client?.name, "Cliente");
    const projectPart = safeFilePart(quote.projectTitle, "Progetto");
    document.title = `${datePart}_${clientPart}_${projectPart}`;

    const restoreTitle = () => {
      document.title = originalTitle;
      window.removeEventListener("afterprint", restoreTitle);
    };

    window.addEventListener("afterprint", restoreTitle);
    window.print();
  };

  const handleExportJson = () => {
    const blob = new Blob([JSON.stringify(quote, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeClient = String(quote.client?.name || "Cliente")
      .replace(/[\\/:*?"<>|#%{}~&]/g, " ")
      .trim()
      .replace(/\s+/g, "_");
    const now = new Date();
    const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}-${String(now.getSeconds()).padStart(2, "0")}`;
    a.download = `${safeClient}_${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("JSON esportato");
  };

  const onImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        const q = Array.isArray(data) ? data[0] : data;
        if (!q || typeof q !== "object") throw new Error("bad");
        applyLoaded({ ...q, id: q.id || uid() });
        toast.success("Preventivo importato");
      } catch {
        toast.error("File JSON non valido");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setLogo(reader.result);
      saveLogo(reader.result);
      toast.success("Logo caricato");
    };
    reader.readAsDataURL(file);
  };

  const resetLogo = () => {
    clearLogo();
    setLogo(DEFAULT_LOGO);
    toast.info("Logo predefinito ripristinato");
  };

  if (!authenticated) {
    return (
      <div className="app-root">
        <Toaster position="top-center" richColors />
        <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
          <form onSubmit={handleLogin} style={{ width: "min(420px, 100%)", background: "white", border: "1px solid #e5e5e5", borderRadius: 14, padding: 28, boxShadow: "0 12px 40px rgba(0,0,0,.08)" }}>
            <div style={{ fontSize: 13, opacity: .6, marginBottom: 6 }}>WE ARE ALBERO</div>
            <h1 style={{ fontSize: 24, margin: "0 0 8px" }}>Quote Builder</h1>
            <p style={{ margin: "0 0 18px", opacity: .7 }}>Inserisci la password dell’archivio online.</p>
            <Input
              type="password"
              value={authPassword}
              onChange={(e) => setAuthPassword(e.target.value)}
              placeholder="Password"
              autoFocus
            />
            <Button type="submit" style={{ width: "100%", marginTop: 12 }} disabled={apiBusy}>
              {apiBusy ? <Loader2 size={15} className="spin" /> : null} Accedi
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="app-root">
      <Toaster position="top-center" richColors />

      {/* HEADER */}
      <header className="app-header no-print" data-testid="app-header">
        <div className="app-brand">
          {logo ? (
            <img src={logo} alt="We Are Albero" className="app-brand-logo" />
          ) : (
            <span className="app-brand-word">
              <em>we are</em>/Albero
            </span>
          )}
          <span className="app-brand-tool">Quote Builder</span>
        </div>

        <div className="app-layout-switch" data-testid="layout-switch">
          {LAYOUTS.map((l) => {
            const Icon = l.icon;
            return (
              <button
                key={l.key}
                type="button"
                className={`layout-btn ${layout === l.key ? "active" : ""}`}
                onClick={() => setLayout(l.key)}
                data-testid={`layout-${l.key}`}
                title={l.label}
              >
                <Icon size={15} />
                <span>{l.label}</span>
              </button>
            );
          })}
        </div>

        <div className="app-actions">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden-input"
            onChange={onLogoFile}
            data-testid="logo-input"
          />

          <span
            className="act-btn act-cloud-on"
            title={apiReady ? "Archivio Google online collegato" : "Collegamento archivio in corso"}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            {apiBusy ? <Loader2 size={15} className="spin" /> : <Cloud size={15} />}
            Online
          </span>

          <Button
            variant="ghost"
            size="sm"
            className="act-btn"
            onClick={openClients}
            data-testid="btn-clients"
          >
            <Users size={15} /> Clienti
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="act-btn"
            onClick={() => fileRef.current?.click()}
            data-testid="btn-upload-logo"
          >
            <Upload size={15} /> Logo
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="act-btn"
            onClick={resetLogo}
            data-testid="btn-reset-logo"
            title="Ripristina logo predefinito"
          >
            <RotateCcw size={15} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="act-btn"
            onClick={openSaved}
            data-testid="btn-open-saved"
          >
            <FolderOpen size={15} /> Salvati
          </Button>
          <input
            ref={importRef}
            type="file"
            accept="application/json,.json"
            className="hidden-input"
            onChange={onImportFile}
            data-testid="import-input"
          />
          <Button
            variant="ghost"
            size="sm"
            className="act-btn"
            onClick={() => importRef.current?.click()}
            data-testid="btn-import-json"
            title="Importa preventivo da file JSON"
          >
            <FileUp size={15} /> Importa
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="act-btn"
            onClick={handleExportJson}
            data-testid="btn-export-json"
            title="Esporta il preventivo corrente in JSON"
          >
            <FileDown size={15} /> Esporta JSON
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="act-btn"
            onClick={handleNew}
            data-testid="btn-new"
          >
            <FilePlus2 size={15} /> Nuovo
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="act-btn act-outline"
            onClick={handleSave}
            data-testid="btn-save"
          >
            <Save size={15} /> Salva
          </Button>
          <Button
            size="sm"
            className="act-btn act-primary"
            onClick={handlePrint}
            data-testid="btn-export"
          >
            <Download size={15} /> Esporta PDF
          </Button>
        </div>
      </header>

      {/* SPLIT */}
      <div className="app-split" data-layout={layout}>
        <aside
          className="app-left no-print"
          data-testid="app-left"
          style={
            layout === "split"
              ? { width: leftWidth, minWidth: leftWidth }
              : undefined
          }
        >
          <FormBuilder
            quote={quote}
            set={set}
            setClient={setClient}
            items={items}
            catalog={catalog}
            onSaveCatalogItem={saveCatalogItem}
            onSaveClient={saveCurrentClient}
            onPickClient={openClients}
          />
        </aside>
        {layout === "split" && (
          <div
            className="app-resizer no-print"
            data-testid="resize-handle"
            onMouseDown={startResize}
          >
            <span className="app-resizer-grip" />
          </div>
        )}
        <main className="app-right" data-testid="app-right">
          <div className="a4-viewport">
            <A4Preview quote={quote} logo={logo} />
          </div>
        </main>
      </div>

      {/* SAVED DIALOG */}
      <Dialog open={savedOpen} onOpenChange={setSavedOpen}>
        <DialogContent className="saved-dialog saved-quotes-dialog" data-testid="saved-dialog">
          <DialogHeader>
            <DialogTitle className="saved-title">
              Preventivi salvati
            </DialogTitle>
          </DialogHeader>
          <div className="saved-sub">
            Google Drive · archivio online
          </div>
          <div className="saved-list">
            {savedLoading && (
              <div className="saved-empty">
                <Loader2 size={16} className="spin" /> Caricamento…
              </div>
            )}
            {!savedLoading && saved.length === 0 && (
              <div className="saved-empty">Nessun preventivo salvato.</div>
            )}
            {!savedLoading &&
              saved.map((q) => (
                <div className="saved-row" key={q.id} data-testid="saved-row">
                  <button
                    className="saved-info saved-quote-info"
                    onClick={() => loadOne(q)}
                    data-testid={`saved-load-${q.number}`}
                  >
                    <span className="saved-quote-top">
                      <span className="saved-date">
                        {q.date ? formatDateIT(q.date) : "Data non disponibile"}
                      </span>
                      <span className="saved-quote-ref">
                        {q.number || "Senza numero"}
                        <span className="saved-badge sb-cloud">drive</span>
                      </span>
                    </span>
                    <span className="saved-client">
                      {q.clientName || "Cliente non indicato"}
                    </span>
                    <span className="saved-project">
                      {q.projectTitle || "Progetto non indicato"}
                    </span>
                  </button>
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* CLIENTS DIALOG */}
      <Dialog open={clientsOpen} onOpenChange={setClientsOpen}>
        <DialogContent className="saved-dialog clients-dialog" data-testid="clients-dialog">
          <DialogHeader>
            <DialogTitle className="saved-title">Rubrica clienti</DialogTitle>
          </DialogHeader>
          <div className="saved-sub">
            <button
              className="clients-add-btn"
              onClick={saveCurrentClient}
              data-testid="btn-save-current-client"
            >
              <UserPlus size={14} /> Salva cliente corrente
            </button>
          </div>
          <Input
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
            placeholder="Cerca cliente…"
            style={{ marginBottom: 10 }}
          />
          <div className="saved-list">
            {clients.length === 0 && (
              <div className="saved-empty">Nessun cliente in rubrica.</div>
            )}
            {clients
              .filter((c) => {
                const q = clientSearch.trim().toLowerCase();
                if (!q) return true;
                return [c.name, c.vat, c.taxCode, c.city, c.email]
                  .filter(Boolean)
                  .some((v) => String(v).toLowerCase().includes(q));
              })
              .map((c, i) => (
              <div className="saved-row client-row" key={i} data-testid="client-row">
                <button
                  className="saved-info client-info"
                  onClick={() => pickClient(c)}
                  data-testid={`client-pick-${i}`}
                >
                  <span className="client-name">{c.name}</span>
                  <span className="client-meta">
                    {(c.vat || c.taxCode) && (
                      <span className="client-code">
                        {c.vat ? `P.IVA ${c.vat}` : `C.F. ${c.taxCode}`}
                      </span>
                    )}
                    {[c.address, [c.cap, c.city, c.province].filter(Boolean).join(" ")]
                      .filter(Boolean)
                      .map((value, j) => (
                        <span key={j}>{value}</span>
                      ))}
                  </span>
                </button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;
